// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import React from 'react'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, CreditCard, TrendingUp, Shield, Building } from 'lucide-react'
import { Card }   from '@/components/ui/Card'
import { Input }  from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/components/ui/Toggle'
import { InfoButton } from '@/components/ui/InfoButton'
import { AlertBanner } from '@/components/feedback/AlertBanner'
import { useAuthStore } from '@/store'
import { saveModuleProgress } from '@/services/profile'
import { ROUTES } from '@/lib/constants/routes'
import { useAutosave }  from '@/hooks/useAutosave'
import {
  loadBank, saveBank, loadInvestments, saveInvestments,
  loadPension, savePension, loadInsurance, saveInsurance,
  EMPTY_BANK, EMPTY_BANK_ENTRY, EMPTY_INVESTMENTS, EMPTY_INVESTMENT,
  EMPTY_PENSION, EMPTY_INSURANCE, EMPTY_INSURANCE_ENTRY, EMPTY_PENSION_FUND, pensionFundList,
  type BankData, type BankEntry, type InvestmentsData, type InvestmentEntry,
  type PensionData, type InsuranceData, type InsuranceEntry, type PensionFund,
  NL_BANKS, BANK_ACCOUNT_TYPES, NL_BROKERS, INTL_BROKERS,
  INVESTMENT_TYPES, CRYPTO_PLATFORMS, NL_PENSION_FUNDS, INSURANCE_TYPES, NL_INSURERS,
} from '@/services/wizard'
import type { SaveStatus } from '@/hooks/useAutosave'

const COUNTRIES = [
  { value: 'NL', label: 'Nederland' }, { value: 'BE', label: 'België' },
  { value: 'DE', label: 'Duitsland' }, { value: 'UK', label: 'Verenigd Koninkrijk' },
  { value: 'US', label: 'VS' }, { value: 'INTL', label: 'Internationaal / Anders' },
]
const YES_NO_UNK = [{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }, { value: 'unknown', label: 'Weet ik niet' }]

interface Props { onSaveStatus: (s: SaveStatus) => void; saveRef?: React.MutableRefObject<(() => Promise<void>) | null> }

export function Step07Financial({ onSaveStatus, saveRef }: Props) {
  const { userId } = useAuthStore()
  const navigate    = useNavigate()
  const [bank,       setBank]       = useState<BankData>(EMPTY_BANK)
  const [inv,        setInv]        = useState<InvestmentsData>(EMPTY_INVESTMENTS)
  const [pension,    setPension]    = useState<PensionData>(EMPTY_PENSION)
  const [insurance,  setInsurance]  = useState<InsuranceData>(EMPTY_INSURANCE)
  const [ready,      setReady]      = useState(false)

  useEffect(() => {
    if (!userId) return
    Promise.all([loadBank(userId), loadInvestments(userId), loadPension(userId), loadInsurance(userId)])
      .then(([b, i, p, ins]) => {
        if (b) setBank(b); if (i) setInv(i)
        if (p) setPension({ ...p, funds: pensionFundList(p) })   // V49-UAT: migreer oud enkel-fonds naar funds[]
        if (ins) setInsurance(ins)
        setReady(true)
      })
  }, [userId])

  const saveAll = useCallback(async () => {
    if (!userId) return
    await Promise.all([saveBank(userId, bank), saveInvestments(userId, inv), savePension(userId, pension), saveInsurance(userId, insurance)])
    const filled = [
      bank.accounts.length > 0,
      inv.hasInvestments === 'yes',
      pension.hasPension !== '',
      insurance.insurances.length > 0,
    ].filter(Boolean).length
    await saveModuleProgress(userId, 'financial', filled, 4)
  }, [userId, bank, inv, pension, insurance])

  // Trigger autosave on any change
  const combined = JSON.stringify({ bank, inv, pension, insurance })
  const saveFn = useCallback(async (_: string) => { await saveAll() }, [saveAll])
  const { status, saveNow } = useAutosave({ data: combined, onSave: saveFn, enabled: ready })
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  // Bank helpers
  function updBank(i: number, field: keyof BankEntry, value: string) {
    setBank(p => { const a = [...p.accounts]; a[i] = { ...a[i], [field]: value }; return { accounts: a } })
  }
  function addBank() { setBank(p => ({ accounts: [...p.accounts, { ...EMPTY_BANK_ENTRY }] })) }
  function removeBank(i: number) { setBank(p => ({ accounts: p.accounts.filter((_, j) => j !== i) })) }

  // Investment helpers
  function updInv(i: number, field: keyof InvestmentEntry, value: string | boolean) {
    setInv(p => { const a = [...p.investments]; a[i] = { ...a[i], [field]: value }; return { ...p, investments: a } })
  }
  function addInv() { setInv(p => ({ ...p, investments: [...p.investments, { ...EMPTY_INVESTMENT }] })) }
  function removeInv(i: number) { setInv(p => ({ ...p, investments: p.investments.filter((_, j) => j !== i) })) }

  // Insurance helpers
  function updIns(i: number, field: keyof InsuranceEntry, value: string) {
    setInsurance(p => { const a = [...p.insurances]; a[i] = { ...a[i], [field]: value }; return { insurances: a } })
  }
  function addIns() { setInsurance(p => ({ insurances: [...p.insurances, { ...EMPTY_INSURANCE_ENTRY }] })) }
  function removeIns(i: number) { setInsurance(p => ({ insurances: p.insurances.filter((_, j) => j !== i) })) }

  // Pension fund helpers (V49-UAT: meerdere fondsen). Spiegelt funds[0] naar top-level voor terugwaartse compatibiliteit.
  function withMirror(funds: PensionFund[]): Partial<PensionData> {
    const first = funds[0]
    return { funds, fundName: first?.fundName ?? '', fundNameOther: first?.fundNameOther ?? '' }
  }
  function updPensionFund(i: number, field: keyof PensionFund, value: string) {
    setPension(p => { const a = [...(p.funds ?? [])]; a[i] = { ...a[i], [field]: value }; return { ...p, ...withMirror(a) } })
  }
  function addPensionFund() { setPension(p => ({ ...p, ...withMirror([...(p.funds ?? []), { ...EMPTY_PENSION_FUND }]) })) }
  function removePensionFund(i: number) { setPension(p => ({ ...p, ...withMirror((p.funds ?? []).filter((_, j) => j !== i)) })) }

  if (!ready) return <div className="flex justify-center py-16"><div className="h-6 w-6 animate-pulse-soft rounded-full bg-sage-200" /></div>

  return (
    <div className="flex flex-col gap-7">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Financieel overzicht</h2>
        <p className="text-sm text-ink-3">Geef uw naasten een overzicht van uw financiële situatie.</p>
      </div>
      <AlertBanner variant="warning" message="Sla hier geen pincodes of wachtwoorden op. Vermeld alleen wat er is en waar het te vinden is." dismissible />

      {/* ── BANKREKENINGEN ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-sage-600" />
          <h3 className="text-base font-semibold text-ink">Bankrekeningen</h3>
          <InfoButton title="Bankrekeningen"
            content={<>
              <p>Na overlijden moeten bankrekeningen worden gemeld. De bank blokkeert de rekening tijdelijk en vraagt om een verklaring van erfrecht.</p>
              <p><strong>IBAN:</strong> optioneel, helpt uw naasten de juiste rekening te vinden.</p>
              <p><strong>Mederekeninghouder:</strong> als uw partner ook op de rekening staat, heeft hij of zij nog toegang.</p>
            </>} />
        </div>

        {bank.accounts.map((acc, i) => (
          <Card key={i}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-ink">Rekening {i + 1}{acc.bankName ? ` - ${acc.bankName}` : ''}</p>
              <button onClick={() => removeBank(i)} className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <div className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Select label="Land" options={COUNTRIES} value={acc.country} onChange={e => updBank(i, 'country', e.target.value)} />
                <Select label="Bank" options={NL_BANKS.map(b => ({ value: b, label: b }))} value={acc.bankName} onChange={e => updBank(i, 'bankName', e.target.value)} placeholder="Kies bank..." />
              </div>
              {acc.bankName === 'Anders' && <Input label="Naam bank" value={acc.bankNameOther} onChange={e => updBank(i, 'bankNameOther', e.target.value)} placeholder="Naam bank" />}
              <div className="grid gap-3 sm:grid-cols-2">
                <Select label="Type rekening" options={BANK_ACCOUNT_TYPES.map(t => ({ value: t, label: t }))} value={acc.accountType} onChange={e => updBank(i, 'accountType', e.target.value)} placeholder="Kies type..." />
                <Input label="IBAN (optioneel)" value={acc.iban} onChange={e => updBank(i, 'iban', e.target.value)} placeholder="NL12 ABCD 0123..." />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Mederekeninghouder" value={acc.coHolder} onChange={e => updBank(i, 'coHolder', e.target.value)} placeholder="Naam (optioneel)" />
                <Input label="Opmerkingen" value={acc.notes} onChange={e => updBank(i, 'notes', e.target.value)} placeholder="Bijv: spaarrekening voor studie..." />
              </div>
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addBank} icon={<Plus className="h-4 w-4" />}>Rekening toevoegen</Button>
      </div>

      {/* ── BELEGGINGEN - alleen tonen als gebruiker 'Ja' heeft gekozen ──── */}
      {inv.hasInvestments === 'yes' && <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-sage-600" />
          <h3 className="text-base font-semibold text-ink">Beleggingen &amp; crypto</h3>
          <InfoButton title="Beleggingen en crypto"
            content={<>
              <p>Na overlijden blokkeert de broker het depot tijdelijk. Erfgenamen hebben een verklaring van erfrecht nodig voor overdracht.</p>
              <p><strong>Crypto:</strong> zonder de wallet-sleutel of seed phrase zijn de crypto-assets permanent verloren. Zorg dat uw naasten weten waar de sleutel bewaard wordt.</p>
            </>} />
        </div>

        {inv.investments.map((item, i) => (
          <Card key={i}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-ink">Belegging {i + 1}</p>
              {inv.investments.length > 1 && <button onClick={() => removeInv(i)} className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
            <div className="flex items-center justify-between mb-3 bg-sand rounded-xl px-3 py-2.5">
              <span className="text-sm font-medium text-ink-2">Dit is cryptocurrency</span>
              <Toggle label="" checked={item.isCrypto} onChange={v => updInv(i, 'isCrypto', v)} />
            </div>
            {item.isCrypto ? (
              <div className="flex flex-col gap-3">
                <Select label="Platform / Exchange" options={CRYPTO_PLATFORMS.map(b => ({ value: b, label: b }))} value={item.cryptoPlatform} onChange={e => updInv(i, 'cryptoPlatform', e.target.value)} placeholder="Kies platform..." />
                {item.cryptoPlatform === 'Anders' && <Input label="Platform naam" value={item.cryptoPlatformOther} onChange={e => updInv(i, 'cryptoPlatformOther', e.target.value)} placeholder="Naam" />}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Select label="Land" options={COUNTRIES} value={item.country} onChange={e => updInv(i, 'country', e.target.value)} />
                  <Select label="Broker / Bank" options={(item.country === 'NL' ? NL_BROKERS : INTL_BROKERS).map(b => ({ value: b, label: b }))} value={item.brokerName} onChange={e => updInv(i, 'brokerName', e.target.value)} placeholder="Kies broker..." />
                </div>
                {item.brokerName === 'Anders' && <Input label="Naam broker" value={item.brokerNameOther} onChange={e => updInv(i, 'brokerNameOther', e.target.value)} placeholder="Naam broker" />}
                <Select label="Type belegging" options={INVESTMENT_TYPES.map(t => ({ value: t, label: t }))} value={item.investmentType} onChange={e => updInv(i, 'investmentType', e.target.value)} placeholder="Kies type..." />
                <div>
                  <label className="block text-sm font-medium text-ink-2 mb-1.5">Actieve hefboomproducten (turbo's, opties)?</label>
                  <div className="flex gap-2">
                    {[{ v: 'yes', l: 'Ja' }, { v: 'no', l: 'Nee' }].map(opt => (
                      <button key={opt.v} type="button" onClick={() => updInv(i, 'hasLeverage', opt.v)}
                        className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-all ${item.hasLeverage === opt.v ? 'border-sage-600 bg-sage-50 text-sage-800' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <Input label="Opmerkingen" value={item.notes} onChange={e => updInv(i, 'notes', e.target.value)} placeholder="Bijv: inloggegevens bij notaris, wallet-sleutel in kluis..." className="mt-3" />
          </Card>
        ))}
        <Button variant="outline" onClick={addInv} icon={<Plus className="h-4 w-4" />}>Belegging toevoegen</Button>
      </div>}

      {/* ── PENSIOEN ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Building className="h-4 w-4 text-sage-600" />
          <h3 className="text-base font-semibold text-ink">Pensioen</h3>
          <InfoButton title="Pensioen"
            content={<>
              <p>Na overlijden moet het pensioenfonds worden geïnformeerd. Het fonds controleert of er recht is op <strong>partnerpensioen</strong> of <strong>wezenpensioen</strong>.</p>
              <p>U kunt uw pensioenopgave inzien via <strong>MijnPensioenoverzicht.nl</strong>. Hier ziet u welke fondsen u heeft opgebouwd.</p>
              <p><strong>AOW:</strong> stopt bij overlijden. Nabestaanden dienen dit te melden bij de SVB. De SVB verwerkt dit via de gemeentelijke registratie.</p>
            </>} />
        </div>
        <Card>
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-2 mb-2">Heeft u pensioen opgebouwd?</label>
              <div className="flex flex-col gap-2">
                {YES_NO_UNK.map(opt => (
                  <button key={opt.value} type="button" onClick={() => setPension(p => ({ ...p, hasPension: opt.value, ...(opt.value === 'yes' && (!p.funds || p.funds.length === 0) ? withMirror([{ ...EMPTY_PENSION_FUND }]) : {}) }))}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${pension.hasPension === opt.value ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'}`}>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${pension.hasPension === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                    <span className="text-sm font-medium text-ink-2">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {pension.hasPension === 'yes' && (
              <div className="flex flex-col gap-3 animate-slide-down">
                {(pension.funds ?? []).map((fund, i) => (
                  <div key={i} className="rounded-xl border border-sand-200 bg-sand-50/40 p-3 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-ink-3">Pensioenfonds {i + 1}</span>
                      {(pension.funds?.length ?? 0) > 1 && (
                        <button type="button" onClick={() => removePensionFund(i)} className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                    <Select label="Pensioenfonds" options={NL_PENSION_FUNDS.map(f => ({ value: f, label: f }))} value={fund.fundName} onChange={e => updPensionFund(i, 'fundName', e.target.value)} placeholder="Kies fonds..." />
                    {(fund.fundName === 'Anders / onbekend' || fund.fundName === 'Anders') && (
                      <Input label="Naam fonds" value={fund.fundNameOther} onChange={e => updPensionFund(i, 'fundNameOther', e.target.value)} placeholder="Naam pensioenfonds" />
                    )}
                    <Input label="Opmerkingen" value={fund.notes} onChange={e => updPensionFund(i, 'notes', e.target.value)} placeholder="Bijv: deelnemersnummer, (oud-)werkgever..." />
                  </div>
                ))}
                <Button variant="outline" onClick={addPensionFund} icon={<Plus className="h-4 w-4" />}>Pensioenfonds toevoegen</Button>

                <button type="button" onClick={() => window.open('https://www.mijnpensioenoverzicht.nl', '_blank')}
                  className="flex items-center justify-center gap-2 rounded-xl border border-sage-200 bg-sage-50 px-4 py-3 text-sm font-medium text-sage-700 hover:bg-sage-100 transition-colors">
                  🔗 Controleer via MijnPensioenoverzicht.nl
                </button>

                <div>
                  <label className="block text-sm font-medium text-ink-2 mb-2">Is er recht op partnerpensioen?</label>
                  <div className="flex gap-2">
                    {YES_NO_UNK.map(opt => (
                      <button key={opt.value} type="button" onClick={() => setPension(p => ({ ...p, hasPartnerPension: opt.value }))}
                        className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-all ${pension.hasPartnerPension === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-2 mb-2">Is er recht op wezenpensioen?</label>
                  <div className="flex gap-2">
                    {YES_NO_UNK.map(opt => (
                      <button key={opt.value} type="button" onClick={() => setPension(p => ({ ...p, hasSurvivorPension: opt.value }))}
                        className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-all ${pension.hasSurvivorPension === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-2 mb-2">Heeft u een pensioenoverzicht opgeslagen?</label>
                  <div className="flex gap-2">
                    {YES_NO_UNK.map(opt => (
                      <button key={opt.value} type="button" onClick={() => setPension(p => ({ ...p, hasPensionOverview: opt.value }))}
                        className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-all ${pension.hasPensionOverview === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {pension.hasPensionOverview === 'yes' && (
                  <Input label="Waar kunnen nabestaanden dit terugvinden?" value={pension.pensionOverviewLocation ?? ''} onChange={e => setPension(p => ({ ...p, pensionOverviewLocation: e.target.value }))} placeholder="Bijv: in de map 'Pensioen', of digitaal in uw kluis..." />
                )}
                <div>
                  <label className="block text-sm font-medium text-ink-2 mb-2">Heeft u een aanvullend pensioen of lijfrente?</label>
                  <div className="flex gap-2">
                    {YES_NO_UNK.map(opt => (
                      <button key={opt.value} type="button" onClick={() => setPension(p => ({ ...p, hasSupplementaryPension: opt.value }))}
                        className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-all ${pension.hasSupplementaryPension === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-2 mb-2">Is er een nabestaandenvoorziening of ANW-hiaatverzekering?</label>
                  <div className="flex gap-2">
                    {YES_NO_UNK.map(opt => (
                      <button key={opt.value} type="button" onClick={() => setPension(p => ({ ...p, hasAnwProvision: opt.value }))}
                        className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-all ${pension.hasAnwProvision === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Input label="Algemene opmerkingen" value={pension.notes} onChange={e => setPension(p => ({ ...p, notes: e.target.value }))} placeholder="Bijv: opgave ligt in de map, AOW via SVB..." />
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── VERZEKERINGEN ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-sage-600" />
          <h3 className="text-base font-semibold text-ink">Verzekeringen</h3>
          <InfoButton title="Verzekeringen"
            content={<>
              <p>Na overlijden moeten verzekeraars worden geïnformeerd. Sommige verzekeringen keren uit (levensverzekering), andere worden beëindigd.</p>
              <p><strong>Zorgverzekering:</strong> eindigt op overlijdensdatum. Nabestaanden hoeven dit meestal niet apart te regelen - de zorgverzekeraar geeft deze informatie doorgaans door aan het CAK.</p>
              <p><strong>Uitvaartverzekering:</strong> dekt uitvaartkosten. Controleer of het een natura- of geldverzekering is.</p>
            </>} />
        </div>

        {insurance.insurances.map((ins, i) => (
          <Card key={i}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-ink">Verzekering {i + 1}{ins.type ? ` - ${ins.type}` : ''}</p>
              <button onClick={() => removeIns(i)} className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <div className="flex flex-col gap-3">
              <Select label="Type verzekering" options={INSURANCE_TYPES.map(t => ({ value: t, label: t }))} value={ins.type} onChange={e => updIns(i, 'type', e.target.value)} placeholder="Kies type..." />
              {/* V40-P1: Uitvaartverzekering hoort bij Uitvaartwensen - geen dubbele invoer */}
              {ins.type === 'Uitvaartverzekering' ? (
                <div className="rounded-xl bg-status-infoBg border border-status-infoBorder px-4 py-3 flex flex-col gap-3">
                  <p className="text-sm text-status-infoText leading-relaxed">
                    Uitvaartverzekering vult u in bij <strong>Uitvaartwensen</strong>. Daar leggen we vast:
                  </p>
                  <ul className="text-xs text-status-infoText space-y-0.5 list-disc list-inside">
                    <li>of u een uitvaartverzekering heeft</li>
                    <li>bij welke verzekeraar</li>
                    <li>polisnummer</li>
                    <li>eventuele instructies voor nabestaanden</li>
                  </ul>
                  <div className="flex gap-2">
                    <button type="button"
                      onClick={() => { removeIns(i); navigate(ROUTES.WISHES_FUNERAL) }}
                      className="flex-1 rounded-xl bg-moss text-white text-xs font-semibold px-3 py-2 hover:bg-moss-light transition-colors">
                      Ga naar Uitvaartwensen
                    </button>
                    <button type="button"
                      onClick={() => removeIns(i)}
                      className="rounded-xl border border-sand-300 text-xs text-ink-3 px-3 py-2 hover:bg-sand-100 transition-colors">
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : (
                <>
              {ins.type === 'Anders' && <Input label="Type omschrijving" value={ins.typeOther} onChange={e => updIns(i, 'typeOther', e.target.value)} placeholder="Omschrijving" />}
              <Select label="Verzekeraar" options={NL_INSURERS.map(s => ({ value: s, label: s }))} value={ins.insurer} onChange={e => updIns(i, 'insurer', e.target.value)} placeholder="Kies verzekeraar..." />
              {ins.insurer === 'Anders' && <Input label="Naam verzekeraar" value={ins.insurerOther} onChange={e => updIns(i, 'insurerOther', e.target.value)} placeholder="Naam" />}
              {/* V37: uitvaart-specifieke velden - bereikbaar alleen bij niet-Uitvaartverzekering */}
              {false && (
                <div className="flex flex-col gap-3 animate-slide-down">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-ink-2">Type uitvaartverzekering</label>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {[
                        { value: 'natura',     label: 'Natura', desc: 'Verzekeraar regelt de uitvaart' },
                        { value: 'kapitaal',   label: 'Kapitaal', desc: 'Geldbedrag wordt uitgekeerd' },
                        { value: 'combinatie', label: 'Combinatie', desc: 'Natura + aanvullend kapitaal' },
                        { value: 'onbekend',   label: 'Onbekend', desc: '' },
                      ].map(opt => (
                        <button key={opt.value} type="button"
                          onClick={() => updIns(i, 'uitvaartType', opt.value)}
                          className={`flex items-start gap-2 rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
                            ins.uitvaartType === opt.value
                              ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
                          }`}>
                          <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                            ins.uitvaartType === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                          }`} />
                          <div>
                            <p className="text-sm font-medium text-ink">{opt.label}</p>
                            {opt.desc && <p className="text-xs text-ink-4">{opt.desc}</p>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-ink-2">Polisdocument aanwezig</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'yes',     label: 'Ja' },
                        { value: 'no',      label: 'Nee' },
                        { value: 'unknown', label: 'Weet niet' },
                      ].map(opt => (
                        <button key={opt.value} type="button"
                          onClick={() => updIns(i, 'docsAvailable', opt.value)}
                          className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm transition-all flex-1 justify-center ${
                            ins.docsAvailable === opt.value
                              ? 'border-sage-600 bg-sage-50 text-sage-800 font-medium'
                              : 'border-sand-300 bg-white text-ink-3 hover:border-sage-300'
                          }`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Polisnummer (optioneel)" value={ins.policyNumber} onChange={e => updIns(i, 'policyNumber', e.target.value)} placeholder="Bijv: 12345678" />
                <Input label="Opmerkingen" value={ins.notes} onChange={e => updIns(i, 'notes', e.target.value)} placeholder="Bijv: loopt af in 2030..." />
              </div>
              <p className="text-xs text-ink-4 leading-relaxed">
                WilsApp legt uw persoonlijke wensen vast. Uw officiële polis staat bij uw verzekeraar.
              </p>
                </>
              )}
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addIns} icon={<Plus className="h-4 w-4" />}>Verzekering toevoegen</Button>
      </div>
    </div>
  )
}
